import { Grid, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { lazy, Suspense, useState } from "react";
import { SubscriptionBreakdown } from "./components/SubscriptionBreakdown";
import {
  PaymentMethods,
  PaymentMethodsList,
} from "./components/PaymentMethodsList";
import { AddPaymentMethodModal } from "./components/AddPaymentMethodModal";
import { formatDate } from "date-fns";
import styled from "@emotion/styled";
import { formatToCurrency } from "@shared/utils/formatters";
import { humanizeEnumValue } from "@shared/utils/text-formatter";
import { ISubscriptionInfo } from "./mange-subscription-models";
import { SubscriptionInvoicesList } from "./components/SubscriptionInvoicesList";
import { RestoreSubscriptionModal } from "./components/RestoreSubscriptionModal";
const PaymentProvider = lazy(() =>
  import("@features/Subscription/components/PaymentProvider").then(
    (module) => ({
      default: module.PaymentProvider,
    }),
  ),
);

interface ManageSubscriptionBase {
  subscriptionInfo: ISubscriptionInfo;
  disabled: boolean;
  readOnlyConfig?: {
    paymentMethod: boolean;
  };

  // ACTIONS
  handleCancelSubscription?: () => Promise<void>;
  handleCancelSubscriptionAtPeriodEnd?: () => Promise<void>;
  handleCancelScheduledSubscriptionCancellation?: () => Promise<void>;
  onRestoreSubscriptionClick?: () => void;
  onUpdateTrialPeriodClick?: () => void;
  handleTurnToFree?: () => Promise<void>;
}

interface SimpleSubscriptionPlan {
  name: string;
  trialEndDate: Date | null;
}

interface ManageSubscriptionAdminProps extends ManageSubscriptionBase {
  role: "admin";
  userInfo: {
    id: string;
    name: string;
    email: string;
    subscriptionPlan: SimpleSubscriptionPlan | undefined;
  };
  handleRestoreSubscription?: (params: { trialDays: number }) => void;
  onCloseRestoreSubscriptionModal: () => void;
  isRestoreSubscriptionModalOpen: boolean;
  handleVoidInvoice?: (invoiceId: string) => void;
  handleRefundInvoice?: (invoiceId: string) => void;
}

interface ManageSubscriptionUserProps extends ManageSubscriptionBase {
  role: "user";
}

type ManageSubscriptionProps =
  | ManageSubscriptionAdminProps
  | ManageSubscriptionUserProps;

const PaymentHistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem 1.75rem;
  border-radius: 0.75rem;
  border: solid 1px ${(p) => p.theme.colors.border2};
`;

const PaymentHistoryItem = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 130px 1fr;
  align-items: center;

  ${(p) => p.theme.media.mobileDown} {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }

  p {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const ManageSubscription = (props: ManageSubscriptionProps) => {
  const {
    role,
    subscriptionInfo,
    readOnlyConfig,
    disabled,
    handleCancelSubscription,
    handleCancelSubscriptionAtPeriodEnd,
    handleCancelScheduledSubscriptionCancellation,
    onRestoreSubscriptionClick,
    onUpdateTrialPeriodClick,
    handleTurnToFree,
  } = props;
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);

  const onAddPaymentMethodClick = () => {
    setPaymentMethodModalOpen(true);
  };

  const onCloseAddPaymentMethod = () => {
    setPaymentMethodModalOpen(false);
  };

  const getPaymentMethodsWithBillingDetails = (): PaymentMethods => {
    if (subscriptionInfo.type !== "paid") return [];

    if (subscriptionInfo.paymentMethod && subscriptionInfo.billingDetails) {
      return [
        {
          ...subscriptionInfo.paymentMethod,
          status: subscriptionInfo.paymentMethodStatus,
          billingDetails: subscriptionInfo.billingDetails,
        },
      ];
    }
    return [];
  };

  return (
    <Grid container columnSpacing={3} rowGap={4}>
      {role === "admin" && (
        <Grid item xs={12}>
          <Typography variant="h6" mb={1}>
            User Info
          </Typography>
          <PaymentHistoryContainer>
            <PaymentHistoryItem>
              <Typography variant="subtitle2">Name</Typography>
              <Typography>{props.userInfo.name}</Typography>
            </PaymentHistoryItem>

            <PaymentHistoryItem>
              <Typography variant="subtitle2">E-mail</Typography>
              <Typography>{props.userInfo.email}</Typography>
            </PaymentHistoryItem>

            <PaymentHistoryItem>
              <Typography variant="subtitle2">Plan Name</Typography>
              <Typography>{props.userInfo.subscriptionPlan?.name}</Typography>
            </PaymentHistoryItem>
          </PaymentHistoryContainer>
        </Grid>
      )}
      <Grid item lg={7} md={12} xs={12}>
        <Typography variant="h6" mb={1}>
          Payment Method
        </Typography>

        <PaymentMethodsList
          paymentMethods={getPaymentMethodsWithBillingDetails()}
          onAddPaymentMethodClick={onAddPaymentMethodClick}
          onEditPaymentMethodClick={onAddPaymentMethodClick}
          requiresPaymentMethod={subscriptionInfo.type === "paid"}
          readOnly={readOnlyConfig?.paymentMethod}
        />
        {!readOnlyConfig?.paymentMethod && (
          <Suspense fallback={null}>
            <PaymentProvider>
              <AddPaymentMethodModal
                open={paymentMethodModalOpen}
                onClose={onCloseAddPaymentMethod}
                billingDetails={subscriptionInfo.billingDetails}
              />
            </PaymentProvider>
          </Suspense>
        )}
      </Grid>
      <Grid item md={7} xs={12}>
        <Typography variant="h6" mb={1}>
          Current Subscription
        </Typography>
        {subscriptionInfo.type === "free" && (
          <SubscriptionBreakdown
            type="free"
            productName={subscriptionInfo.name}
          />
        )}
        {subscriptionInfo.type === "paid" && (
          <SubscriptionBreakdown
            type="paid"
            price={subscriptionInfo.price}
            productName={subscriptionInfo.name}
            currency="USD"
            trialEndDate={subscriptionInfo.trialEndDate}
            hasPaymentMethod={subscriptionInfo.paymentMethodStatus === "Valid"}
            nextPaymentDate={subscriptionInfo.nextPaymentDate}
            lastPaymentDate={subscriptionInfo.lastPaymentDate}
            cancellationDate={subscriptionInfo.cancellationDate}
            scheduledCancellationDate={
              subscriptionInfo.scheduledCancellationDate
            }
            credits={subscriptionInfo.credits ?? 0}
            estimatedPaymentMethodRequiredDate={
              subscriptionInfo.estimatedPaymentMethodRequiredDate
            }
            disabled={disabled}
            userInfo={role === "admin" ? props.userInfo : undefined}
            handleCancelSubscription={handleCancelSubscription}
            handleCancelSubscriptionAtPeriodEnd={
              handleCancelSubscriptionAtPeriodEnd
            }
            handleCancelScheduledSubscriptionCancellation={
              handleCancelScheduledSubscriptionCancellation
            }
            onRestoreSubscriptionClick={onRestoreSubscriptionClick}
            onUpdateTrialPeriodClick={onUpdateTrialPeriodClick}
            handleTurnToFree={handleTurnToFree}
          />
        )}
        {subscriptionInfo.type === "invalid" && (
          <SubscriptionBreakdown
            type="invalid"
            productName={subscriptionInfo.name}
            errorDescription={subscriptionInfo.errorDescription}
          />
        )}
      </Grid>

      {subscriptionInfo.type === "paid" && (
        <Grid item md={5} xs={12}>
          <Typography variant="h6" mb={1}>
            Details
          </Typography>
          <PaymentHistoryContainer>
            <PaymentHistoryItem>
              <Typography variant="body1" fontWeight={500}>
                Payment Status
              </Typography>
              <Typography variant="body2" color="primary">
                {humanizeEnumValue(subscriptionInfo.paymentStatus)}
              </Typography>
            </PaymentHistoryItem>

            <PaymentHistoryItem>
              <Typography variant="body1" fontWeight={500}>
                Credits{" "}
                <Tooltip
                  title={
                    subscriptionInfo.credits > 0
                      ? "You have subscription credits available. These will automatically be applied to your next payment, reducing the amount charged."
                      : "You currently have no credits. Any charges for your subscription will be billed in full to your payment method."
                  }
                >
                  <InfoOutlinedIcon fontSize="small" color="secondary" />
                </Tooltip>
              </Typography>
              <Typography variant="body2" color="primary">
                US{formatToCurrency(subscriptionInfo.credits / 100)}
              </Typography>
            </PaymentHistoryItem>

            <PaymentHistoryItem>
              <Typography variant="body1" fontWeight={500}>
                Last Payment
              </Typography>
              <Typography variant="body2" color="primary">
                {subscriptionInfo.lastPaymentDate ? (
                  formatDate(subscriptionInfo.lastPaymentDate, "MM/dd/yyyy ")
                ) : (
                  <Typography variant="body2" component="span">
                    No payment yet
                  </Typography>
                )}
              </Typography>
            </PaymentHistoryItem>

            <PaymentHistoryItem>
              <Typography variant="body1" fontWeight={500}>
                Next Payment
              </Typography>
              <Typography variant="body2" color="primary">
                {subscriptionInfo.nextPaymentDate ? (
                  formatDate(subscriptionInfo.nextPaymentDate, "MM/dd/yyyy ")
                ) : (
                  <Typography variant="body2" component="span">
                    No payment required
                  </Typography>
                )}
              </Typography>
            </PaymentHistoryItem>
          </PaymentHistoryContainer>
        </Grid>
      )}
      {/* 
      <Grid item lg={7} md={12} xs={12}>
        <Typography variant="h6" mb={1}>
          Payment Method
        </Typography>

        <PaymentMethodsList
          paymentMethods={getPaymentMethodsWithBillingDetails()}
          onAddPaymentMethodClick={onAddPaymentMethodClick}
          onEditPaymentMethodClick={onAddPaymentMethodClick}
          requiresPaymentMethod={subscriptionInfo.type === "paid"}
          readOnly={readOnlyConfig?.paymentMethod}
        />
        {!readOnlyConfig?.paymentMethod && (
          <PaymentProvider>
            <AddPaymentMethodModal
              open={paymentMethodModalOpen}
              onClose={onCloseAddPaymentMethod}
              billingDetails={subscriptionInfo.billingDetails}
            />
          </PaymentProvider>
        )}
      </Grid> */}

      {subscriptionInfo.type === "paid" && (
        <Grid item xs={12}>
          <Typography variant="h6" mb={1}>
            Invoice History
          </Typography>
          {role === "user" && (
            <SubscriptionInvoicesList
              invoices={subscriptionInfo.invoices}
              actionsEnabled={false}
            />
          )}
          {role === "admin" && (
            <SubscriptionInvoicesList
              invoices={subscriptionInfo.invoices}
              actionsEnabled={true}
              handleVoidInvoice={props.handleVoidInvoice}
              handleRefundInvoice={props.handleRefundInvoice}
            />
          )}
        </Grid>
      )}

      {role === "admin" && !!props.handleRestoreSubscription && (
        <RestoreSubscriptionModal
          productName={subscriptionInfo.name}
          userInfo={props.userInfo}
          open={props.isRestoreSubscriptionModalOpen}
          isLoading={disabled}
          onClose={props.onCloseRestoreSubscriptionModal}
          onRestoreSubscription={props.handleRestoreSubscription}
        />
      )}
    </Grid>
  );
};
